import type { IndexGroupScope, IndexItem } from './types'
import { IndexRow, IndexTile, Highlight, StatusBadge } from './IndexRow'
import { IndexPreview } from './IndexPreview'
import { KIND_LABELS, followLink, itemKey, itemSubtitle, tileName, type IndexSection, type IndexView } from './view'
import styles from './IndexSearch.module.css'

export interface IndexResultsProps {
  view: IndexView
  query: string
  activeKey: string | null
  /** Id prefix for result rows, unique per Index instance. */
  idPrefix: string
  onNavigate: (href: string) => void
  onHover: (key: string) => void
  onSeeAll: (scope: IndexGroupScope) => void
}

function TopHit({ item, query, id, active, onNavigate, onHover }: {
  item: IndexItem
  query: string
  id: string
  active: boolean
  onNavigate: (href: string) => void
  onHover: () => void
}) {
  return (
    <a
      id={id}
      href={item.href}
      className={styles.topHit}
      data-active={active || undefined}
      aria-current={active || undefined}
      onClick={(e) => followLink(e, item.href, onNavigate)}
      onMouseEnter={onHover}
    >
      <IndexTile name={tileName(item)} logoUrl={item.logoUrl} color={item.color} round={item.kind === 'player'} size="lg" />
      <span className={styles.topHitText}>
        <span className={styles.topHitName}>
          <Highlight name={item.name} query={query} />
        </span>
        <span className={styles.rowSub}>{itemSubtitle(item)}</span>
        {item.kind === 'competition' && <StatusBadge competition={item} />}
      </span>
      <span className={styles.kindLabel}>{KIND_LABELS[item.kind]}</span>
    </a>
  )
}

export function IndexResults({ view, query, activeKey, idPrefix, onNavigate, onHover, onSeeAll }: IndexResultsProps) {
  const rowId = (key: string) => `${idPrefix}-${key}`
  const row = (section: string, item: IndexItem, extra: { showKind?: boolean; compact?: boolean } = {}) => {
    const key = itemKey(section, item)
    return (
      <IndexRow
        key={key}
        id={rowId(key)}
        item={item}
        query={query}
        active={activeKey === key}
        onNavigate={onNavigate}
        onHover={() => onHover(key)}
        {...extra}
      />
    )
  }
  const sectionHead = (s: IndexSection) => (
    <div className={styles.sectionHead}>
      <h2 className={styles.heading}>
        {s.label} <span className={styles.headingCount}>{s.total}</span>
      </h2>
      {view.layout !== 'list' && s.scope && s.hasMore && (
        <button type="button" className={styles.seeAll} onClick={() => onSeeAll(s.scope as IndexGroupScope)}>
          See all
        </button>
      )}
    </div>
  )

  if (view.layout === 'empty') {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyTitle}>No results for “{query}”</p>
        <p className={styles.rowSub}>Check the spelling, or try a player, team, club, league or competition name.</p>
      </div>
    )
  }

  const top = view.topHit
  const topKey = top ? itemKey('top', top) : ''
  const topHit = top && (
    <section aria-label="Top hit" className={styles.stack}>
      <h2 className={styles.heading}>Top hit</h2>
      <TopHit
        item={top}
        query={query}
        id={rowId(topKey)}
        active={activeKey === topKey}
        onNavigate={onNavigate}
        onHover={() => onHover(topKey)}
      />
    </section>
  )

  if (view.layout === 'mixed') {
    return (
      <div className={styles.mixed}>
        <div className={styles.mixedTop}>
          {topHit}
          <section aria-label="Top results" className={styles.stack}>
            <h2 className={styles.heading}>Top results · every type</h2>
            <div className={styles.panel}>{view.topResults.map((it) => row('blend', it, { showKind: true }))}</div>
          </section>
        </div>
        <div className={styles.shelves}>
          {view.sections.map((s) => (
            <section key={s.id} aria-label={s.label} className={styles.shelf}>
              {sectionHead(s)}
              {s.items.map((it) => row(s.id, it, { compact: true }))}
            </section>
          ))}
        </div>
      </div>
    )
  }

  // list and focused: a column of results with a preview of the highlighted one.
  const active = view.order.find((o) => o.key === activeKey)?.item ?? view.order[0]?.item
  return (
    <div className={styles.split}>
      <div className={styles.stack}>
        {topHit}
        {view.sections.map((s) => (
          <section key={s.id} aria-label={s.label} className={styles.group}>
            {sectionHead(s)}
            {s.items.map((it) => row(s.id, it))}
            {view.layout === 'list' && s.hasMore && (
              <p className={styles.more}>
                Showing {s.items.length} of {s.total} — add a word to narrow it down.
              </p>
            )}
          </section>
        ))}
      </div>
      {active && <IndexPreview item={active} onNavigate={onNavigate} />}
    </div>
  )
}
